import { useState, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'

interface PrintOptions {
  pageSize?: string
  orientation?: string
  documentTitle?: string
  onAfterPrint?: () => void
}

interface UsePrintReturn<T extends HTMLElement = HTMLDivElement> {
  showPrintPreview: () => void
  closePrintPreview: () => void
  isPrintPreviewOpen: boolean
  printRef: React.RefObject<T>
  handleDirectPrint: () => void
  printOptions: PrintOptions
  setPrintOptions: React.Dispatch<React.SetStateAction<PrintOptions>>
}

export const usePrint = <T extends HTMLElement = HTMLDivElement>(
  initialOptions?: PrintOptions
): UsePrintReturn<T> => {
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState(false)
  const [printOptions, setPrintOptions] = useState<PrintOptions>({
    pageSize: initialOptions?.pageSize || 'a4',
    orientation: initialOptions?.orientation || 'portrait',
    documentTitle: initialOptions?.documentTitle || 'Print',
    onAfterPrint: initialOptions?.onAfterPrint,
  })

  const printRef = useRef<T>(null)

  const showPrintPreview = () => setIsPrintPreviewOpen(true)
  const closePrintPreview = () => setIsPrintPreviewOpen(false)

  const handleDirectPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printOptions.documentTitle,
    onAfterPrint: printOptions.onAfterPrint,
    pageStyle: `
      @page {
        size: ${printOptions.pageSize} ${printOptions.orientation};
        margin: 0.5cm;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  })

  return {
    showPrintPreview,
    closePrintPreview,
    isPrintPreviewOpen,
    printRef,
    handleDirectPrint,
    printOptions,
    setPrintOptions,
  }
}
